import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycCaseCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type {
  CreateKycCaseRequestDto,
  KycCaseDto,
} from "@/features/kyc/api/dto";

/**
 * Ouvre un dossier KYC au statut DRAFT (`POST /KycCases/add`) — étape 4 du
 * parcours d'inscription. Idempotency-Key OBLIGATOIRE (guide §4.7) :
 * clé persistée par empreinte du corps ; 409 = attendre puis rejouer ;
 * 422 = nouvelle tentative.
 */
export const useCreateKycCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "kyc-cases", "create"],
    mutationFn: (body: CreateKycCaseRequestDto) =>
      mutateIdempotent<KycCaseDto>({
        scopeId: "kyc.cases.add",
        url: kycEndpoints.kycCases.add,
        body,
      }),
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, kycCaseCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateKycCase failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
